// ABOUTME: Firestore access for the Quick To-do widget's list.
// ABOUTME: Lives at users/{uid}/widgets/quickTodo; shape is validated by firestore.rules.

import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "./firebase";

export interface TodoItem {
  id: string;
  text: string;
  done: boolean;
}

// Mirrors the cap in firestore.rules `isValidWidgetState`.
export const MAX_TODO_ITEMS = 200;

function todoRef(userId: string) {
  return doc(db, `users/${userId}/widgets`, "quickTodo");
}

function isTodoItem(value: unknown): value is TodoItem {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as TodoItem).id === "string" &&
    typeof (value as TodoItem).text === "string" &&
    typeof (value as TodoItem).done === "boolean"
  );
}

export const todoService = {
  /**
   * Streams the list. The callback receives `null` when no document exists
   * yet, so the caller can seed it from whatever this browser already holds.
   */
  subscribe(
    userId: string,
    onData: (items: TodoItem[] | null) => void,
    onError: (error: Error) => void
  ): () => void {
    return onSnapshot(
      todoRef(userId),
      (snap) => {
        if (!snap.exists()) {
          // "Missing" is only trustworthy from the server. The SDK's cache can
          // report a document it has simply never fetched (fresh device,
          // offline); seeding from that would overwrite the real list when
          // the connection returns. Show nothing until the server answers.
          onData(snap.metadata?.fromCache ? [] : null);
          return;
        }
        const raw = snap.data().items;
        onData(Array.isArray(raw) ? raw.filter(isTodoItem) : []);
      },
      onError
    );
  },

  async save(userId: string, items: TodoItem[]): Promise<void> {
    await setDoc(todoRef(userId), {
      items: items.slice(0, MAX_TODO_ITEMS),
      updatedAt: Date.now(),
    });
  },
};
